import React from 'react'
import { fromJS, List, Map } from 'immutable'
import _set from 'lodash/set'
import { useQuery } from '@apollo/client'
import _compact from 'lodash/compact'
import _get from 'lodash/get'
import {
  GET_PERSONS,
} from '../../services/content/query'
import {
  CONTENT_IMAGE_TYPE_HEADSHOT,
  createTitleGroupId,
  getFormattedContentData,
  getOrderedContent,
} from '../../services/content/index'
import { APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST } from '../../services/apollo-graphql/index'
import { MAX_ROW_ITEMS, getPmSectionTabs } from '../../services/pm-section/index'
import { parseQuery } from '../../services/inbound-tracking/index'
import { stringifyQuery } from '../../test/nock'
import { get as getConfig } from '../../config/index'
import Picture, { IMAGE_LOADING_LAZY, IMAGE_TYPE_WEBP } from '../Picture/index'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])

/**
 * The Share Page Expert component
 * Displays information for a single expert including title and headshot image.
 *
 * @param {Object} props - Component props
 * @param {Immutable.Map} props.data - Immutable Map containing expert data (title, images, etc.)
 * @returns {JSX.Element} Single expert display component
 */
function SharePageExpert (props) {
  const { data } = props
  const title = data.get('title', '') || ''
  const images = data.get('images', List()) || List()
  const imageData = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_HEADSHOT) || Map()
  const image = imageData.get('url', '')
  const imageWidth = '144'
  const imageHeight = '144'

  let imageUrl = ''
  let fallbackImageUrl = ''

  try {
    if (optimizedImagesEnabled) {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    }
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  return (
    <div className="share-page__expert">
      <div className="share-page__expert-hero">
        <div className="share-page__expert-hero-image">
          {
            optimizedImagesEnabled
              ? <Picture
                fallbackSrc={fallbackImageUrl}
                width={imageWidth}
                height={imageHeight}
                alt={title}
                loading={IMAGE_LOADING_LAZY}
                sources={[
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: imageUrl,
                  },
                ]}
              />
              : <img
                src={image}
                loading="lazy"
                alt={title}
              />
          }
        </div>
      </div>
      <div className="share-page__expert-title">{title}</div>
    </div>
  )
}

/**
 * The Share Page Experts component
 * Renders a section with Page Manager (PM) experts information on the Share Page.
 *
 * @param {Object} props - Component props
 * @param {string} props.language - Current user/page language code
 * @param {Object} props.pmSection - Page Manager section state
 * @param {string} props.sectionId - Section ID
 * @returns {JSX.Element} Share Page Experts section component
 */
function SharePageExperts (props) {
  const {
    language,
    pmSection,
    sectionId,
  } = props
  const tabs = getPmSectionTabs({ sectionId, pmSection })
  // Get the second tab (index 1) for people/experts
  const peopleTab = tabs.get(1)
  const content = peopleTab ? peopleTab.get('tabContent', List()) : List()
  const titleGroupIds = _compact(
    content.map((item) => createTitleGroupId(item.get('contentId'), item.get('contentType'))).toJS(),
  )

  const { data, loading } = useQuery(GET_PERSONS, {
    variables: {
      language: [language, '_primary'],
      titleGroupIds,
      imageType: CONTENT_IMAGE_TYPE_HEADSHOT,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  })

  if (!titleGroupIds.length) return null

  const contentList = _get(data, ['contentList', 'content'], []) || []
  const orderedList = getOrderedContent(titleGroupIds, contentList, 'titleGroupId')
  const items = fromJS(orderedList) || List()

  return (
    <div className="share-page__experts">
      {loading
        ? items.slice(0, MAX_ROW_ITEMS).map((id) => (
          <div key={id} className="share-page__experts-item">
            <div className="share-page__tile-placeholder" />
          </div>
        ))
        : items.map((person, index) => (
          <div key={index} className="share-page__experts-item" data-index={index}>
            <SharePageExpert data={getFormattedContentData(person)} />
          </div>
        ))
      }
    </div>
  )
}

export default SharePageExperts
